import { Dialect } from '../dialect.js';
import { keywordDisplayForm } from '../lexer/keyword-display.js';
import type { Keyword } from '../lexer/keywords.js';
import type {
  BinaryExpression,
  BinaryOperator,
  Declaration,
  Expression,
  ForStatement,
  IfStatement,
  LValue,
  Program,
  RepeatUntilStatement,
  Statement,
  Subprogram,
  SwitchCaseValue,
  SwitchStatement,
  VariableDeclarator,
  WhileStatement,
} from '../parser/ast.js';

/**
 * "Αντίστροφος parser" — μετατρέπει AST πίσω σε πηγαίο κώδικα, στη σωστή
 * γραφή (κεφαλαία/πεζά) της δοσμένης διαλέκτου. Χρησιμοποιείται από τις
 * μετατροπές δομών (βλ. `src/transform/`) για να δείξουν το "πριν" και το
 * "μετά" ως πραγματικό, επαναναλύσιμο κείμενο κώδικα.
 *
 * Δεν είναι 1-1 αντιστρέψιμο με το πρωτότυπο κείμενο — το AST δεν κρατάει
 * λεπτομέρειες εμφάνισης (ποιο είδος εισαγωγικών, πόσο κενό υπήρχε) — παράγει
 * πάντα μια καθαρή, κανονικοποιημένη μορφή (μονά εισαγωγικά, 2 κενά ανά
 * επίπεδο). Εξαίρεση: ποιο από τα 3 συνώνυμα Γράψε/Εκτύπωσε/Εμφάνισε
 * γράφτηκε ΔΙΑΤΗΡΕΙΤΑΙ (βλ. `WriteStatement.verb` στο ast.ts).
 */

const INDENT_UNIT = '  ';

function indent(level: number): string {
  return INDENT_UNIT.repeat(level);
}

function kw(keyword: Keyword, dialect: Dialect): string {
  return keywordDisplayForm(keyword, dialect);
}

// --- Εκφράσεις --------------------------------------------------------------

function binaryPrecedence(op: BinaryOperator): number {
  switch (op) {
    case 'Η':
      return 1;
    case 'ΚΑΙ':
      return 2;
    case '=':
    case '<>':
    case '<':
    case '<=':
    case '>':
    case '>=':
      return 4;
    case '+':
    case '-':
      return 5;
    case '*':
    case '/':
    case 'DIV':
    case 'MOD':
      return 6;
    case '^':
      return 8;
  }
}

// Ακέραιες, όχι κλασματικές, τιμές — ώστε το `prec + 1` που χρησιμοποιεί το
// δεξί μέλος ενός αριστερόστροφου τελεστή (βλ. `printBinaryExpression`) να
// μην πέφτει ΜΕΣΑ σε ένα επίπεδο μοναδιαίου τελεστή (π.χ. `x ΚΑΙ ΟΧΙ y` δεν
// πρέπει να παίρνει περιττές παρενθέσεις γύρω από το `ΟΧΙ y`).
const NOT_PRECEDENCE = 3;
const UNARY_MINUS_PRECEDENCE = 7;

function displayBinaryOperator(op: BinaryOperator, dialect: Dialect): string {
  switch (op) {
    case 'DIV':
      return kw('DIV', dialect);
    case 'MOD':
      return kw('MOD', dialect);
    case 'ΚΑΙ':
      return kw('ΚΑΙ', dialect);
    case 'Η':
      return kw('Η', dialect);
    default:
      return op;
  }
}

function printBinaryExpression(expr: BinaryExpression, dialect: Dialect, minPrec: number): string {
  const prec = binaryPrecedence(expr.operator);
  const rightAssoc = expr.operator === '^';
  const leftText = printExpression(expr.left, dialect, rightAssoc ? prec + 1 : prec);
  const rightText = printExpression(expr.right, dialect, rightAssoc ? prec : prec + 1);
  const text = `${leftText} ${displayBinaryOperator(expr.operator, dialect)} ${rightText}`;
  return prec < minPrec ? `(${text})` : text;
}

/**
 * @param minPrec Η ελάχιστη προτεραιότητα που «χωράει» εδώ χωρίς παρενθέσεις
 *   — καθορίζεται από τον γονέα (π.χ. το δεξί μέλος ενός αριστερόστροφου
 *   τελεστή χρειάζεται minPrec = prec+1 για να διατηρηθεί η σωστή ομαδοποίηση).
 */
export function printExpression(expr: Expression, dialect: Dialect, minPrec = 0): string {
  switch (expr.kind) {
    case 'NumberLiteral':
      return String(expr.value);
    case 'StringLiteral':
      return `'${expr.value}'`;
    case 'BooleanLiteral':
      return kw(expr.value ? 'ΑΛΗΘΗΣ' : 'ΨΕΥΔΗΣ', dialect);
    case 'Identifier':
      return expr.name;
    case 'ArrayAccess':
      return `${expr.array.name}[${expr.indices.map((i) => printExpression(i, dialect)).join(', ')}]`;
    case 'FunctionCall':
      return `${expr.callee.name}(${expr.args.map((a) => printExpression(a, dialect)).join(', ')})`;
    case 'UnaryExpression': {
      if (expr.operator === 'ΟΧΙ') {
        const text = `${kw('ΟΧΙ', dialect)} ${printExpression(expr.operand, dialect, NOT_PRECEDENCE)}`;
        return NOT_PRECEDENCE < minPrec ? `(${text})` : text;
      }
      const text = `-${printExpression(expr.operand, dialect, UNARY_MINUS_PRECEDENCE)}`;
      return UNARY_MINUS_PRECEDENCE < minPrec ? `(${text})` : text;
    }
    case 'BinaryExpression':
      return printBinaryExpression(expr, dialect, minPrec);
  }
}

export function printLValue(target: LValue, dialect: Dialect): string {
  if (target.kind === 'Identifier') return target.name;
  return `${target.array.name}[${target.indices.map((i) => printExpression(i, dialect)).join(', ')}]`;
}

// --- Εντολές -----------------------------------------------------------------

function printBlock(statements: readonly Statement[], dialect: Dialect, level: number): string {
  return statements.map((s) => printStatement(s, dialect, level)).join('\n');
}

function printIfStatement(stmt: IfStatement, dialect: Dialect, level: number): string {
  const pad = indent(level);
  const lines: string[] = [`${pad}${kw('ΑΝ', dialect)} ${printExpression(stmt.condition, dialect)} ${kw('ΤΟΤΕ', dialect)}`];
  lines.push(printBlock(stmt.thenBranch, dialect, level + 1));
  for (const branch of stmt.elseIfBranches) {
    lines.push(`${pad}${kw('ΑΛΛΙΩΣ_ΑΝ', dialect)} ${printExpression(branch.condition, dialect)} ${kw('ΤΟΤΕ', dialect)}`);
    lines.push(printBlock(branch.body, dialect, level + 1));
  }
  if (stmt.elseBranch !== null) {
    lines.push(`${pad}${kw('ΑΛΛΙΩΣ', dialect)}`);
    lines.push(printBlock(stmt.elseBranch, dialect, level + 1));
  }
  lines.push(`${pad}${kw('ΤΕΛΟΣ_ΑΝ', dialect)}`);
  return lines.join('\n');
}

function printForStatement(stmt: ForStatement, dialect: Dialect, level: number): string {
  const pad = indent(level);
  const stepText = stmt.step !== null ? ` ${kw('ΜΕ_ΒΗΜΑ', dialect)} ${printExpression(stmt.step, dialect)}` : '';
  return [
    `${pad}${kw('ΓΙΑ', dialect)} ${stmt.variable.name} ${kw('ΑΠΟ', dialect)} ${printExpression(stmt.from, dialect)} ${kw('ΜΕΧΡΙ', dialect)} ${printExpression(stmt.to, dialect)}${stepText}`,
    printBlock(stmt.body, dialect, level + 1),
    `${pad}${kw('ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ', dialect)}`,
  ].join('\n');
}

function printWhileStatement(stmt: WhileStatement, dialect: Dialect, level: number): string {
  const pad = indent(level);
  return [
    `${pad}${kw('ΟΣΟ', dialect)} ${printExpression(stmt.condition, dialect)} ${kw('ΕΠΑΝΑΛΑΒΕ', dialect)}`,
    printBlock(stmt.body, dialect, level + 1),
    `${pad}${kw('ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ', dialect)}`,
  ].join('\n');
}

function printRepeatUntilStatement(stmt: RepeatUntilStatement, dialect: Dialect, level: number): string {
  const pad = indent(level);
  return [
    `${pad}${kw('ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ', dialect)}`,
    printBlock(stmt.body, dialect, level + 1),
    `${pad}${kw('ΜΕΧΡΙΣ_ΟΤΟΥ', dialect)} ${printExpression(stmt.condition, dialect)}`,
  ].join('\n');
}

function printSwitchCaseValue(value: SwitchCaseValue, dialect: Dialect): string {
  if (value.kind === 'CaseRange') return `${printExpression(value.from, dialect)}..${printExpression(value.to, dialect)}`;
  if (value.kind === 'CaseComparison') return `${value.operator} ${printExpression(value.value, dialect)}`;
  return printExpression(value, dialect);
}

function printSwitchStatement(stmt: SwitchStatement, dialect: Dialect, level: number): string {
  const pad = indent(level);
  const lines: string[] = [`${pad}${kw('ΕΠΙΛΕΞΕ', dialect)} ${printExpression(stmt.discriminant, dialect)}`];
  for (const c of stmt.cases) {
    lines.push(`${pad}${kw('ΠΕΡΙΠΤΩΣΗ', dialect)} ${c.values.map((v) => printSwitchCaseValue(v, dialect)).join(', ')}`);
    lines.push(printBlock(c.body, dialect, level + 1));
  }
  if (stmt.elseBody !== null) {
    lines.push(`${pad}${kw('ΠΕΡΙΠΤΩΣΗ', dialect)} ${kw('ΑΛΛΙΩΣ', dialect)}`);
    lines.push(printBlock(stmt.elseBody, dialect, level + 1));
  }
  lines.push(`${pad}${kw('ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ', dialect)}`);
  return lines.join('\n');
}

export function printStatement(stmt: Statement, dialect: Dialect, level = 0): string {
  const pad = indent(level);
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return `${pad}${printLValue(stmt.target, dialect)} ← ${printExpression(stmt.value, dialect)}`;
    case 'ReadStatement':
      return `${pad}${kw('ΔΙΑΒΑΣΕ', dialect)} ${stmt.targets.map((t) => printLValue(t, dialect)).join(', ')}`;
    case 'WriteStatement':
      // stmt.verb θυμάται ποιο από τα 3 συνώνυμα (Γράψε/Εκτύπωσε/Εμφάνισε,
      // μόνο Ψευδογλώσσα) χρησιμοποιήθηκε· undefined (π.χ. ΓΛΩΣΣΑ, που δεν
      // έχει συνώνυμα) σημαίνει το κοινό Γράψε.
      return `${pad}${kw(stmt.verb ?? 'ΓΡΑΨΕ', dialect)} ${stmt.values.map((v) => printExpression(v, dialect)).join(', ')}`;
    case 'SwapStatement':
      return `${pad}${kw('ΑΝΤΙΜΕΤΑΘΕΣΕ', dialect)} ${printLValue(stmt.left, dialect)}, ${printLValue(stmt.right, dialect)}`;
    case 'IfStatement':
      return printIfStatement(stmt, dialect, level);
    case 'ForStatement':
      return printForStatement(stmt, dialect, level);
    case 'WhileStatement':
      return printWhileStatement(stmt, dialect, level);
    case 'RepeatUntilStatement':
      return printRepeatUntilStatement(stmt, dialect, level);
    case 'SwitchStatement':
      return printSwitchStatement(stmt, dialect, level);
    case 'CallStatement': {
      const argsText = stmt.args.length > 0 ? `(${stmt.args.map((a) => printExpression(a, dialect)).join(', ')})` : '';
      return `${pad}${kw('ΚΑΛΕΣΕ', dialect)} ${stmt.callee.name}${argsText}`;
    }
  }
}

/** Τυπώνει μια λίστα εντολών, μία ανά γραμμή, με σωστή εσοχή (2 κενά ανά επίπεδο). */
export function printStatements(statements: readonly Statement[], dialect: Dialect, level = 0): string {
  return printBlock(statements, dialect, level);
}

// --- Πρόγραμμα (ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ) ---------------------------------

function printVariableDeclarator(d: VariableDeclarator, dialect: Dialect): string {
  if (d.dimensions.length === 0) return d.name;
  return `${d.name}[${d.dimensions.map((dim) => printExpression(dim, dialect)).join(', ')}]`;
}

function printDeclaration(decl: Declaration, dialect: Dialect): string {
  if (decl.kind === 'ConstantDeclaration') {
    return `  ${decl.name} = ${printExpression(decl.value, dialect)}`;
  }
  const typeKw = kw(decl.declaredType, dialect);
  return `  ${typeKw}: ${decl.declarators.map((d) => printVariableDeclarator(d, dialect)).join(', ')}`;
}

/** Δηλώσεις (ΣΤΑΘΕΡΕΣ/ΜΕΤΑΒΛΗΤΕΣ) — κοινές για το κύριο πρόγραμμα και για κάθε υποπρόγραμμα. */
export function printDeclarationsSection(declarations: readonly Declaration[], dialect: Dialect, lines: string[]): void {
  const constants = declarations.filter((d) => d.kind === 'ConstantDeclaration');
  if (constants.length > 0) {
    lines.push(kw('ΣΤΑΘΕΡΕΣ', dialect));
    for (const decl of constants) lines.push(printDeclaration(decl, dialect));
  }
  const variables = declarations.filter((d) => d.kind === 'VariableDeclaration');
  if (variables.length > 0) {
    lines.push(kw('ΜΕΤΑΒΛΗΤΕΣ', dialect));
    for (const decl of variables) lines.push(printDeclaration(decl, dialect));
  }
}

/** ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ — μόνο ΓΛΩΣΣΑ (βλ. ast.ts, `Subprogram`). */
export function printSubprogram(sub: Subprogram, dialect: Dialect): string {
  const params = sub.parameters.join(', ');
  const lines: string[] =
    sub.kind === 'ProcedureDeclaration'
      ? [`${kw('ΔΙΑΔΙΚΑΣΙΑ', dialect)} ${sub.name}(${params})`]
      : [`${kw('ΣΥΝΑΡΤΗΣΗ', dialect)} ${sub.name}(${params}): ${kw(sub.returnType, dialect)}`];

  printDeclarationsSection(sub.declarations, dialect, lines);

  lines.push(kw('ΑΡΧΗ', dialect));
  lines.push(printStatements(sub.body, dialect, 1));
  lines.push(kw(sub.kind === 'ProcedureDeclaration' ? 'ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ' : 'ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ', dialect));
  return lines.join('\n');
}

/** Τυπώνει ολόκληρο το πρόγραμμα, με το περίβλημα ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ — χρήσιμο για να δείξει κανείς το "πριν"/"μετά" μιας μετατροπής δομής ως πλήρη, επαναναλύσιμο κώδικα. */
export function printProgram(program: Program): string {
  const dialect = program.dialect;

  if (program.dialect === Dialect.GLOSSA) {
    const lines: string[] = [`${kw('ΠΡΟΓΡΑΜΜΑ', dialect)} ${program.name}`];
    printDeclarationsSection(program.declarations, dialect, lines);
    lines.push(kw('ΑΡΧΗ', dialect));
    lines.push(printStatements(program.body, dialect, 1));
    lines.push(kw('ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ', dialect));
    for (const sub of program.subprograms) {
      lines.push('');
      lines.push(printSubprogram(sub, dialect));
    }
    return lines.join('\n');
  }

  const lines: string[] = [`${kw('ΑΛΓΟΡΙΘΜΟΣ', dialect)} ${program.name}`];
  if (program.dataVariables.length > 0) {
    lines.push(`  ${kw('ΔΕΔΟΜΕΝΑ', dialect)} // ${program.dataVariables.join(', ')} //`);
  }
  lines.push(printStatements(program.body, dialect, 1));
  if (program.resultVariables.length > 0) {
    lines.push(`  ${kw('ΑΠΟΤΕΛΕΣΜΑΤΑ', dialect)} // ${program.resultVariables.join(', ')} //`);
  }
  lines.push(`${kw('ΤΕΛΟΣ', dialect)} ${program.name}`);
  return lines.join('\n');
}
